/**
 * HTTP Client avec retry logic, timeout intelligent et gestion d'erreurs
 */

interface FetchOptions extends RequestInit {
  timeout?: number;
  maxRetries?: number;
  retryDelay?: number;
  retryBackoff?: boolean;
}

interface HttpResponse<T> {
  ok: boolean;
  status: number;
  data?: T;
  error?: string;
}

// Logger centralisé
const logger = {
  error: (msg: string, data?: any) => {
    console.error(`[HTTP Error] ${msg}`, data);
    // TODO: Envoyer à Sentry en production
  },
  warn: (msg: string, data?: any) => {
    console.warn(`[HTTP Warn] ${msg}`, data);
  },
  info: (msg: string) => {
    console.info(`[HTTP] ${msg}`);
  },
};

/**
 * Fetch avec timeout
 */
const fetchWithTimeout = (
  url: string,
  options: FetchOptions = {}
): Promise<Response> => {
  const { timeout = 10000, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(url, {
    ...fetchOptions,
    signal: controller.signal,
  })
    .finally(() => clearTimeout(timeoutId))
    .catch((error) => {
      if (error.name === 'AbortError') {
        logger.warn(`Request timeout après ${timeout}ms`, { url });
        throw new Error(`Timeout après ${timeout}ms`);
      }
      throw error;
    });
};

/**
 * Retry avec backoff exponentiel
 */
const fetchWithRetry = async (
  url: string,
  options: FetchOptions = {}
): Promise<Response> => {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    retryBackoff = true,
    timeout = 10000,
    ...fetchOptions
  } = options;

  let lastError: Error | null = null;
  const retryableStatuses = [408, 429, 500, 502, 503, 504];

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, {
        ...fetchOptions,
        timeout,
      });

      // Retry sur certains status codes
      if (!response.ok && retryableStatuses.includes(response.status)) {
        if (attempt < maxRetries) {
          const delay = retryBackoff
            ? retryDelay * Math.pow(2, attempt)
            : retryDelay;
          logger.warn(`Tentative ${attempt + 1}/${maxRetries} après ${delay}ms`, {
            url,
            status: response.status,
          });
          await new Promise((r) => setTimeout(r, delay));
          continue;
        }
      }

      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxRetries) {
        const delay = retryBackoff
          ? retryDelay * Math.pow(2, attempt)
          : retryDelay;
        logger.warn(`Tentative ${attempt + 1}/${maxRetries} après ${delay}ms`, {
          url,
          error: lastError.message,
        });
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
    }
  }

  throw (
    lastError ||
    new Error(`Échec après ${maxRetries} tentatives pour ${url}`)
  );
};

/**
 * HTTP Client principal
 */
export class HttpClient {
  private baseUrl: string;
  private defaultOptions: FetchOptions;

  constructor(baseUrl: string = '', defaultOptions: FetchOptions = {}) {
    this.baseUrl = baseUrl;
    this.defaultOptions = {
      timeout: 10000,
      maxRetries: 3,
      retryDelay: 1000,
      retryBackoff: true,
      ...defaultOptions,
    };
  }

  private getUrl(endpoint: string): string {
    return this.baseUrl ? `${this.baseUrl}${endpoint}` : endpoint;
  }

  private async handleResponse<T>(response: Response): Promise<HttpResponse<T>> {
    try {
      const contentType = response.headers.get('content-type');
      let data;

      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        logger.error(`HTTP ${response.status}`, { url: response.url, data });
        return {
          ok: false,
          status: response.status,
          error: data?.error || data || response.statusText,
        };
      }

      return {
        ok: true,
        status: response.status,
        data,
      };
    } catch (error) {
      logger.error('Response parsing failed', error);
      return {
        ok: false,
        status: response.status,
        error: String(error),
      };
    }
  }

  async get<T = any>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<HttpResponse<T>> {
    try {
      logger.info(`GET ${endpoint}`);
      const response = await fetchWithRetry(this.getUrl(endpoint), {
        ...this.defaultOptions,
        ...options,
        method: 'GET',
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      logger.error(`GET ${endpoint}`, error);
      return {
        ok: false,
        status: 0,
        error: String(error),
      };
    }
  }

  async post<T = any>(
    endpoint: string,
    body?: any,
    options: FetchOptions = {}
  ): Promise<HttpResponse<T>> {
    try {
      logger.info(`POST ${endpoint}`);
      const response = await fetchWithRetry(this.getUrl(endpoint), {
        ...this.defaultOptions,
        ...options,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      logger.error(`POST ${endpoint}`, error);
      return {
        ok: false,
        status: 0,
        error: String(error),
      };
    }
  }

  async put<T = any>(
    endpoint: string,
    body?: any,
    options: FetchOptions = {}
  ): Promise<HttpResponse<T>> {
    try {
      logger.info(`PUT ${endpoint}`);
      const response = await fetchWithRetry(this.getUrl(endpoint), {
        ...this.defaultOptions,
        ...options,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      logger.error(`PUT ${endpoint}`, error);
      return {
        ok: false,
        status: 0,
        error: String(error),
      };
    }
  }

  async delete<T = any>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<HttpResponse<T>> {
    try {
      logger.info(`DELETE ${endpoint}`);
      const response = await fetchWithRetry(this.getUrl(endpoint), {
        ...this.defaultOptions,
        ...options,
        method: 'DELETE',
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      logger.error(`DELETE ${endpoint}`, error);
      return {
        ok: false,
        status: 0,
        error: String(error),
      };
    }
  }
}

// Export instance par défaut
export const httpClient = new HttpClient('api');
